import React, { useState } from 'react'
import { Form, message } from 'antd'
import { useMutation, useApolloClient } from '@apollo/client'
import {
  CREATE_CATEGORY,
  UPDATE_MERCHANT_ARRANGEMENT_SETTINGS,
  UPDATE_PRODUCT
} from '../ProductQueries'
import { ProductInfo, CategoryInfo, MerchantSettings } from '@slerp/controls'
import ProductForm from './ProductForm'
import { uuid } from 'uuidv4'
import { useSession } from '@slerp/accounts'
const { useForm } = Form

interface ProductProps {
  categories: Array<CategoryInfo>
  product: ProductInfo
  refetchCategories: () => void
  settings: MerchantSettings
  refetchSettings: () => void
  refetchProducts: () => void
}

interface GraphQLError {
  message: string
}

const Product = (props: ProductProps) => {
  const {
    categories,
    product,
    refetchCategories,
    settings,
    refetchSettings,
    refetchProducts
  } = props
  const defaultVariant = product.children.find(
    (variant) => variant.default_variant === true
  )!
  const [productDetails, setProductDetails] = useState({
    id: product.id,
    variant_id: product.default_variant_id,
    name: product.name,
    description: product.description,
    category_id: product.category_id,
    category_name: '',
    image: defaultVariant.image,
    default_price: defaultVariant.price,
    default_vat: product.default_vat,
    default_limit: product.default_limit,
    height: product.height,
    width: product.width,
    weight: product.weight,
    length: product.length,
    restriction_id: defaultVariant.restrictions.id,
    alcoholic: defaultVariant.restrictions.alcoholic,
    published: defaultVariant.published,
    popular: false
  })
  const client = useApolloClient()
  const [form] = useForm()
  const { merchant } = useSession()
  const [newCategory, setNewCategory] = useState(false)
  const [orderSwitch, setOrderSwitch] = useState(product.default_limit > 0)
  const [updateMerchantArrangementSettings] = useMutation(
    UPDATE_MERCHANT_ARRANGEMENT_SETTINGS
  )
  const [updateProduct] = useMutation(UPDATE_PRODUCT)
  const [createCategory] = useMutation(CREATE_CATEGORY)

  const handleNewCategory = () => {
    const categoryId = uuid()

    message.loading('Creating Category... Please Wait.')
    createCategory({
      variables: {
        merchant_id: merchant.id,
        id: categoryId,
        slug: productDetails.category_name.replace(/\s/g, '-').toLowerCase(),
        name: productDetails.category_name
      }
    })
      .then((result) => {
        message.destroy()
        message.success('Category Created!', 1)
        refetchCategories()

        const key =
          parseInt(Object.keys(settings.category_arrangement).pop()!) + 1
        const updatedSettings = {
          ...settings,
          category_arrangement: {
            ...settings.category_arrangement,
            [key]: categoryId
          }
        }
        updateMerchantArrangementSettings({
          variables: {
            merchant: merchant.slug,
            arrangement: updatedSettings
          }
        })
          .then((result) => {
            refetchSettings()
            setProductDetails({
              ...productDetails,
              category_id: categoryId
            })
            setNewCategory(!newCategory)
          })
          .catch(() => {
            message.destroy()
            message.error('Unable to update Merchant arrangment settings', 1)
          })
      })
      .catch(() => {
        message.destroy()
        client.resetStore()
        message.error('Unable to create new category', 1)
      })
  }

  const handleChange = (
    target: string,
    value: string | number | boolean | null
  ) => {
    setProductDetails({
      ...productDetails,
      [target]: value
    })
  }

  const onFinish = () => {
    if (form.validateFields()) {
      message.loading('Updating Product... Please Wait.')

      updateProduct({
        variables: {
          slug: productDetails.name.replace(/\s/g, '-').toLowerCase(),
          id: productDetails.id,
          variant_id: productDetails.variant_id,
          name: productDetails.name,
          description: productDetails.description,
          category_id: productDetails.category_id,
          default_vat: productDetails.default_vat,
          price: productDetails.default_price,
          default_limit: orderSwitch ? productDetails.default_limit : 0,
          published: productDetails.published,
          restrictions: {
            id: productDetails.restriction_id,
            alcoholic: productDetails.alcoholic
          }
        }
      })
        .then((result) => {
          message.destroy()
          client.resetStore()
          message.success('Product Updated!', 1)
        })
        .catch((err) => {
          const { graphQLErrors } = err
          let errMessages = ''
          if (graphQLErrors) {
            errMessages += graphQLErrors
              .map((gqlErr: GraphQLError) => gqlErr.message)
              .toString()
          }
          message.destroy()
          client.resetStore()
          message.error(`Unable to update due to: ${errMessages}`, 3)
        })
    }
  }

  return (
    <ProductForm
      productDetails={productDetails}
      onFinish={onFinish}
      form={form}
      handleChange={handleChange}
      handleNewCategory={handleNewCategory}
      categories={categories}
      newCategory={newCategory}
      setNewCategory={setNewCategory}
      orderSwitch={orderSwitch}
      setOrderSwitch={setOrderSwitch}
      refetchProducts={refetchProducts}
    />
  )
}

export default Product
